import React, {
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useMemo,
} from 'react';

import BasicButton from '../../../../../components/buttons/basicButton';
// components
import TextInput from '../../../../../components/inputs/textInput';
import ConfirmPrompt from '../../../../../components/modals/confirmPrompt';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../../../constants/aampeTheme';
// icons
import ICONS from '../../../../../constants/icons';

import useVariantDelete from 'hooks/variants/useVariantDelete';

// models
import { VariantUpdateModel } from '../../../../../models/alternate.model';
import { FormulaStatus } from '../../../../../models/formula.model';

import ImageAlternateLabelDropdown from './ImageAlternateLabelDropdown';
import ImageAlternatePreview from './ImageAlternatePreview';

interface ImageAlternateBodyProps {
    data: VariantUpdateModel[];
    formulaId: number;
    setAlternates?: Dispatch<SetStateAction<VariantUpdateModel[]>>;
    isReadOnly?: boolean;
    showPreview?: boolean;
    handleDeleteMaster: () => void;
    handleAddOne?: () => void;
    variantId?: string;
    showDeleteConfirmation: boolean;
    setShowDeleteConfirmation: (value: boolean) => void;
    handleAdd?: () => void;
    isLoading?: boolean;
    status?: FormulaStatus;
}

const ImageAlternateBody: FC<React.PropsWithChildren<ImageAlternateBodyProps>> =
    ({
        data,
        formulaId,
        setAlternates,
        isReadOnly = false,
        showPreview = true,
        handleDeleteMaster,
        handleAddOne,
        variantId,
        showDeleteConfirmation,
        setShowDeleteConfirmation,
        handleAdd,
        isLoading,
        status,
    }) => {
        const sorted = useMemo<VariantUpdateModel[]>(
            () =>
                data.sort((a, b) => Number(b.is_master) - Number(a.is_master)),
            [data],
        );

        const alternateDeleteMutation = useVariantDelete(
            variantId as string,
            formulaId,
        );

        const handleDelete = useCallback(
            (item: VariantUpdateModel) => {
                if (item.is_master) {
                    setShowDeleteConfirmation(true);
                    return;
                }

                if (showPreview) {
                    setAlternates?.((prev) =>
                        prev.map((pItem) => {
                            if (pItem.id === item.id) {
                                return {
                                    ...pItem,
                                    deleted: true,
                                };
                            }
                            return pItem;
                        }),
                    );
                } else {
                    alternateDeleteMutation.mutate(
                        (item as any).alternate_variant_id,
                    );
                }
            },
            [
                alternateDeleteMutation,
                setAlternates,
                setShowDeleteConfirmation,
                showPreview,
            ],
        );

        const isDraft = useMemo<boolean>(
            () => status === FormulaStatus.Draft,
            [status],
        );

        return (
            <>
                {showDeleteConfirmation ? (
                    <ConfirmPrompt
                        message={`Are you sure you want to delete this Component?`}
                        description={`Any alternates that you've created below will be lost.`}
                        type={'warning'}
                        abortHandler={() => {
                            setShowDeleteConfirmation(false);
                        }}
                        confirmHandler={handleDeleteMaster}
                        style={{ marginBottom: aampeTheme.spacing(2) }}
                    />
                ) : (
                    <Container>
                        {sorted.map((item) => (
                            <AlternateItem key={item.id}>
                                {showPreview && (
                                    <ImageAlternatePreview text={item.text} />
                                )}
                                <TitleContainer>
                                    <TitleWithInfo>
                                        <TitleName>Image URL </TitleName>
                                    </TitleWithInfo>

                                    <StyledTextField
                                        placeholder="Image URL"
                                        value={item.text}
                                        wrapperStyle={{
                                            padding: aampeTheme.spacing(0),
                                        }}
                                        isDisabled={isReadOnly && !isDraft}
                                        onChange={(e) => {
                                            setAlternates?.((prev) =>
                                                prev.map((pItem) => {
                                                    if (pItem.id === item.id) {
                                                        return {
                                                            ...pItem,
                                                            text: e.target
                                                                .value,
                                                        };
                                                    }
                                                    return pItem;
                                                }),
                                            );
                                        }}
                                    />
                                </TitleContainer>
                                <TitleContainer>
                                    <TitleWithInfo>
                                        <TitleName>Label </TitleName>
                                    </TitleWithInfo>
                                    <ImageAlternateLabelDropdown
                                        selected={item.label_id}
                                        isDisabled={isReadOnly && !isDraft}
                                        formulaId={formulaId}
                                        handleUpdateLabel={(labelId) =>
                                            setAlternates?.((prev) =>
                                                prev.map((pItem) => {
                                                    if (pItem.id === item.id) {
                                                        return {
                                                            ...pItem,
                                                            label_id: labelId,
                                                        };
                                                    }
                                                    return pItem;
                                                }),
                                            )
                                        }
                                    />
                                </TitleContainer>
                                {(showPreview || isDraft) && !isReadOnly && (
                                    <DeleteIcon
                                        onClick={() => handleDelete(item)}
                                    />
                                )}
                            </AlternateItem>
                        ))}
                        {!isReadOnly && (
                            <Action onClick={handleAddOne}>
                                <ICONS.plus size={14} />
                                Add alternate image
                            </Action>
                        )}
                        {isDraft && !!sorted.length && (
                            <StyledButton
                                name={'Save'}
                                action={handleAdd}
                                isDisabled={isLoading}
                            />
                        )}
                    </Container>
                )}
            </>
        );
    };

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
});
const StyledButton = styled(BasicButton)({
    alignSelf: 'end',
    marginTop: aampeTheme.spacing4(3),
});

const AlternateItem = styled.div({
    fontWeight: 'normal',
    display: 'flex',
    columnGap: aampeTheme.spacing(2),
});

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: aampeTheme.spacing(1),
});
const TitleName = styled.span({
    fontSize: aampeTheme.size.font.default,
    fontWeight: 700,
    lineHeight: '20px',
    color: aampeTheme.colors.gray600,
});

const TitleWithInfo = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing(1),
});

const StyledTextField = styled(TextInput)({
    width: '100%',
    padding: aampeTheme.spacing(0),
    '& .MuiInputBase-root': {
        backgroundColor: aampeTheme.colors.white,
        borderRadius: aampeTheme.borderRadius.default,
        borderColor: aampeTheme.colors.bordersWG01,
        minHeight: '38px',
        '&:hover fieldset': {
            borderColor: aampeTheme.colors.solidA01,
        },
    },
});

const DeleteIcon = styled(ICONS.trashX)({
    cursor: 'pointer',
});

const Action = styled.div({
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    columnGap: aampeTheme.spacing(1),
    cursor: 'pointer',
    color: aampeTheme.colors.solidA01,
});

export default ImageAlternateBody;
